import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'How long does it take to repay credit card debt?',
    a: 'It depends on your balance, interest rate, and how much you pay each month. On a £3,000 balance at 22% APR, paying only the minimum can take over 10 years. Paying an extra £50–£100/month can cut that down to 2–3 years and save hundreds in interest.',
  },
  {
    q: 'What is the fastest way to repay debt in the UK?',
    a: 'The debt avalanche method — paying off the highest interest rate debt first — saves the most money. The debt snowball (smallest balance first) builds momentum faster. Both beat paying minimums only. Even an extra £20–£50/month makes a significant difference.',
  },
  {
    q: 'How much will I save by paying extra on my debt each month?',
    a: 'Typically a great deal. On a £5,000 credit card at 20% APR, adding £50/month to your minimum payment can save over £1,500 in interest and cut your repayment time by 4+ years. Use the calculator above to see your exact figures.',
  },
  {
    q: 'Should I use the debt snowball or avalanche method to repay debt?',
    a: 'Avalanche is mathematically optimal — you pay less interest overall. Snowball gives faster psychological wins by eliminating small debts first. If motivation is your challenge, start with snowball. If you want pure efficiency, choose avalanche. This calculator shows both.',
  },
];


export default function DebtRepaymentCalculator() {
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Debt Repayment Calculator | Pay Off Debt Faster</title>
        <meta name="description" content="Calculate exactly how long it will take to repay your debts. Free debt repayment calculator — no signup, instant results." />
        <link rel="canonical" href="https://clearmydebt.app/debt-repayment-calculator/" />
        <meta property="og:title" content="Free Debt Repayment Calculator | Pay Off Debt Faster" />
        <meta property="og:description" content="Calculate exactly how long it will take to repay your debts. Free debt repayment calculator — no signup, instant results." />
        <meta property="og:url" content="https://clearmydebt.app/debt-repayment-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <link rel="alternate" hreflang="en-gb" href="https://clearmydebt.app/debt-repayment-calculator/" />
        <link rel="alternate" hreflang="en-au" href="https://clearmydebt.app/debt-repayment-calculator/" />
        <link rel="alternate" hreflang="x-default" href="https://clearmydebt.app/debt-repayment-calculator/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Debt Repayment Calculator — Find Your Debt-Free Date
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Enter your debts below and see exactly when you'll finish your debt repayment —
          and how much interest you'll save with a credit card repayment plan.
        </p>
      </div>

      <Calculator
        locale="en-gb"
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How this debt repayment calculator works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Enter each debt — your balance, annual interest rate, and minimum monthly payment.
          Then add any extra amount you can put toward repayment. The calculator shows your
          debt-free date, total interest paid, and how different repayment strategies compare.
          It supports UK and Australian repayment formats and currency.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          Credit card repayment: why minimum payments cost so much
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Credit card interest compounds monthly on your outstanding balance. At 20% APR —
          common for UK credit cards — a £4,000 balance paid at minimums only can take
          12+ years and cost more than £3,500 in interest. Adding even £30–£50/month to
          your repayment dramatically shortens the timeline and cuts total interest paid.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators exclude="/debt-repayment-calculator/" />
    </div>
  );
}
